import * as React from "react";
import { Spinner } from "@bizform/primereact/components/spinner/Spinner";
import { SpinnerProps } from "@bizform/primereact/spinner";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { WindowKey } from "@bizform/type/model/model/WindowKey.model";
import { ExceptionType } from "@bizform/type/model/model/Exception.model";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import { ComponentHelper, Debug, FunctionHelper, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit, I18N } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = SpinnerProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    value?: number;
    tabindex?: number;
    name?: string;
    step?: number;
    min?: number;
    max?: number;
    // maxlength?: number;
    // disabled?: boolean;
    readonly?: boolean;
    // size?: number;
    style?: object;
    className?: string;
    // inputId?: string;
    inputStyle?: object;
    inputClassName?: string;
    printStyle?: object;
    printClassName?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    // event
    onChange?(e: {value: number}): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi
 * @class BfSpinner
 * @description BfSpinner component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<number>>()
export class BfSpinner extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_Q4sxcwy7vpd1utUaVbXtR110RMXMeyG",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.spinner.label,
        description: I18n.componentElement.spinner.description,
        icon: "",
        dataType: [DataType.INTEGER],
        property: null
    };

    focus(): void {
        if (this._input) {
            this._input.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: number): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: number): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: null
                }
            };
            this.setState(newState);
        } else if ( typeof value === "number" || typeof value === "string") {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value
                }
            };
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfSpinner)
    static init(): ComponentElement<number> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _spinner: any;
    private _tab: boolean;
    private _input: HTMLInputElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: null
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._keyDown = this._keyDown.bind(this);
        this._onFocus = this._onFocus.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (this._spinner && this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, Array.from(this._spinner.querySelectorAll("input")).concat(Array.from(this._spinner.querySelectorAll("button"))), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentWillUnmount(): void {
        this._spinner = null;
        if (this._input) {
            this._input.removeEventListener("keydown", this._keyDown);
            this._input.removeEventListener("blur", this._keyDown);
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private async _onChangeEvent(e): Promise<void> {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            await FunctionHelper.timeout();
            const v = Number(e.target.value);
            if (this._tab && v !== this.state.wrappedCompState.value) {
                e.target.value = this.state.wrappedCompState.value ? String(this.state.wrappedCompState.value) : "";
                this._tab = false;
            }
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: e.target.value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    private _onFocus() {
        const {value} = this.state.wrappedCompState;
        if (isNaN(Number(value)) || Number(value) % 1 !== 0) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: null
                }
            };
            this.setState(newState, this._callbackSetState);
        } else if (Number(value) < this.state.wrappedCompState.min || Number(value) > this.state.wrappedCompState.max) {
            let data = Number(value) < this.state.wrappedCompState.min ? this.state.wrappedCompState.min : this.state.wrappedCompState.max;
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: data
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        return (
            <span onFocus={this._onFocus}>
                <Spinner
                    {...newProps}
                    key={key}
                    id={this.elementId}
                    ref={this._setRef}
                />
            </span>
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readonly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        const { value } = this.state.wrappedCompState;

        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            if (this._input) {
                this._input.removeEventListener("keydown", this._keyDown);
                this._input.removeEventListener("blur", this._keyDown);
            }
            this._spinner = ref && ref.element ? ref.element : ref;
            if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
                const d = this._spinner;
                this._input = d.querySelector("input");
                if (this.accessibility) {
                    const up: HTMLInputElement = d.querySelector(".p-spinner-button-up");
                    const down: HTMLInputElement = d.querySelector(".p-spinner-button-down");
                    this._input.setAttribute("aria-label", I18N.translate("{$I._app.accessibility.component.spinner.value}", {min: this.state.wrappedCompState.min, max: this.state.wrappedCompState.max}));
                    up.setAttribute("aria-label", I18N.translate("{$I._app.accessibility.component.spinner.doUp}", {step: this.state.wrappedCompState.step}));
                    down.setAttribute("aria-label", I18N.translate("{$I._app.accessibility.component.spinner.doDown}", {step: this.state.wrappedCompState.step}));
                    up.setAttribute(DomAttribute.ACCESSIBILITYCUSTOM, "");
                    down.setAttribute(DomAttribute.ACCESSIBILITYCUSTOM, "");
                }
                this._input.addEventListener("keydown", this._keyDown);
                this._input.addEventListener("blur", this._keyDown);
            }
            this.readyComponent();
        }
    }

    private _keyDown(e: KeyboardEvent) {
        if (e.key === "Tab" || e.type === "blur") {
            this._tab = true;
        } else {
            this._tab = false;
        }
        if (e.type === "blur") {
            const v = Number((e.target as any).value);
            if ((e.target as any).value) {
                if (this.state.wrappedCompState.min < 0 && (e.target as any).value === "-") {
                } else if ((this.state.wrappedCompState.value as any) === "" || (this.state.wrappedCompState.value as any) === null || (this.state.wrappedCompState.value as any) === undefined) {
                    (e.target as any).value = "";
                } else {
                    if (isNaN(v) || Number(v) % 1 !== 0) {
                        window[WindowKey.CORELIBRARY].ExceptionHandler.toast(ExceptionType.INFO, I18N.translate("{$I._app.accessibility.component.spinner.invalidValue}"));
                        (e.target as any).value = "";
                    } else if ((this.state.wrappedCompState.min || this.state.wrappedCompState.min === 0) && v < this.state.wrappedCompState.min) {
                        window[WindowKey.CORELIBRARY].ExceptionHandler.toast(ExceptionType.INFO, I18N.translate("{$I._app.accessibility.component.spinner.smallerValue}"));
                        (e.target as any).value = this.state.wrappedCompState.min;
                    } else if ((this.state.wrappedCompState.max || this.state.wrappedCompState.max === 0) && v > this.state.wrappedCompState.max) {
                        window[WindowKey.CORELIBRARY].ExceptionHandler.toast(ExceptionType.INFO, I18N.translate("{$I._app.accessibility.component.spinner.biggerValue}"));
                        (e.target as any).value = this.state.wrappedCompState.max;
                    }
                    const newState = {
                        wrappedCompState: {
                            ...this.state.wrappedCompState,
                            value: (e.target as any).value
                        }
                    };
                    this.setState(newState, this._callbackSetState);
                }
            }
        }
    }
}

